import { fg } from '@atlaskit/platform-feature-flags/fg';

import { type AggResponse, fetchAgg } from '../../../common/utils/fetch-agg';
import { inMemoryCache } from '../../../utils/caching/in-memory';

const spacesCache = inMemoryCache<AggResponse<ConfluenceSpaceKeysToIdsQueryResponse>>();

const GRAPHQL_OPERATION_NAME = 'ConfluenceSpaceKeysToIdsQuery';

const getConfluenceSpaceKeysToIdsQuery = (useAgg: boolean) =>
	useAgg
		? `
query ${GRAPHQL_OPERATION_NAME}($cloudId: ID!, $spaceKeys: [String]) {
  spaces(cloudId: $cloudId, spaceKeys: $spaceKeys) {
    nodes {
      id
    }
  }
}
`
		: `
query ${GRAPHQL_OPERATION_NAME}($spaceKeys: [String]) {
  spaces(spaceKeys: $spaceKeys) {
    nodes {
      id
    }
  }
}
`;

export interface ConfluenceSpaceKeysToIdsQueryResponseNode {
	id: string | null;
}

export interface ConfluenceSpaceKeysToIdsQueryResponseNodes {
	nodes: (ConfluenceSpaceKeysToIdsQueryResponseNode | null)[] | null;
}

export interface ConfluenceSpaceKeysToIdsQueryResponse {
	spaces: ConfluenceSpaceKeysToIdsQueryResponseNodes | null;
}

export interface ConfluenceSpaceKeysToIdsQueryVariables {
	/** Only sent to AGG, which has no `cloudId` in the endpoint path. */
	cloudId?: string;
	spaceKeys?: (string | null)[] | null;
}

export interface ConfluenceSpaceKeysToIdsQueryProps {
	spaceKeys: string[];
	cloudId: string;
	headers?: Record<string, string>;
}

export const confluenceSpaceKeysToIdsQuery = ({
	spaceKeys,
	cloudId,
	headers,
}: ConfluenceSpaceKeysToIdsQueryProps): Promise<
	AggResponse<ConfluenceSpaceKeysToIdsQueryResponse>
> => {
	return spacesCache.inMemoryDecorator(`${spaceKeys.join(',')}-${cloudId}`, async () => {
		const useAgg = fg('rovo_search_confluence_graphql_via_agg');
		const response = await fetchAgg<
			ConfluenceSpaceKeysToIdsQueryVariables,
			ConfluenceSpaceKeysToIdsQueryResponse
		>({
			graphQuery: getConfluenceSpaceKeysToIdsQuery(useAgg),
			variables: useAgg ? { cloudId, spaceKeys } : { spaceKeys },
			operationName: GRAPHQL_OPERATION_NAME,
			// AGG serves Confluence GraphQL from the default gateway endpoint. When the gate is off we
			// override the URL to cc-graphql, which carries `cloudId` in the path instead.
			aggAbsoluteUrl: useAgg ? undefined : `/gateway/api/ex/confluence/${cloudId}/graphql`,
			headers,
		});

		const hasErrors = response.errors?.some((error) => error);
		if (hasErrors) {
			return Promise.reject(response);
		}
		return response;
	});
};
